import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

/**
 * Base controller for image upload functionality with cropping
 * 
 * USAGE PATTERNS:
 * 
 * 1. EXTEND FOR CUSTOM FUNCTIONALITY:
 *    import ImageUploadBaseController from "./image_upload_base_controller"
 *    export default class extends ImageUploadBaseController { ... }
 * 
 * 2. OVERRIDE METHODS FOR CUSTOMIZATION:
 *    - getCropperOptions(): Customize aspect ratio, viewMode, etc.
 *    - onImageCropped(croppedImageURL, croppedFile): Handle post-crop actions
 *    - onImageRemoved(): Handle image removal
 *    - getErrorMessage(fileType): Custom error messages
 * 
 * 3. REQUIRED TARGETS:
 *    - uploadField: file input element
 *    - imagePreview: img element for cropper
 *    - modal: modal container for cropping
 * 
 * 4. OPTIONAL TARGETS:
 *    - uploadWrapper: upload area (hidden after crop)
 *    - actionsWrapper: edit/remove actions (shown after crop)
 *    - imageContainer: background image container
 */
export default class extends Controller {
  static targets = [
    "uploadField",
    "uploadWrapper", 
    "actionsWrapper",
    "imagePreview",
    "modal",
    "imageContainer"
  ]

  connect() {
    this.cropper = null
    this.allowedTypes = [
      "image/jpeg",
      "image/png", 
      "image/gif",
      "image/webp",
      "image/jpg",
      "image/bmp",
      "image/svg+xml"
    ]
  }

  disconnect() {
    this.destroyCropper()
  }

  // Core upload flow methods
  triggerImageInput(event) {
    if (this.hasUploadFieldTarget) {
      this.uploadFieldTarget.click()
    }
  }

  handleImageSelect(event) {
    const input = event.target
    const file = input.files[0]

    if (!file) return

    if (!this.isValidFileType(file.type)) {
      input.value = ""
      this.showError(this.getErrorMessage(file.type))
      return
    }

    this.previewImageForCropping(file)
  }

  previewImageForCropping(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (this.hasImagePreviewTarget) {
        this.imagePreviewTarget.src = e.target.result
        this.openCropModal()
        this.initializeCropper()
      }
    }
    reader.readAsDataURL(file)
  }

  // Cropper management
  initializeCropper() {
    if (this.hasImagePreviewTarget) {
      this.destroyCropper()
      this.cropper = new Cropper(this.imagePreviewTarget, this.getCropperOptions())
    }
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  // Modal management
  openCropModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('d-none')
    }
  }

  closeCropModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('d-none')
    }
    this.destroyCropper()
  }

  // Crop and save
  cropAndSaveImage() {
    if (!this.cropper) return

    const canvas = this.cropper.getCroppedCanvas()
    
    canvas.toBlob((blob) => {
      const croppedFile = new File([blob], "cropped-image.png", {
        type: "image/png"
      })

      // Update the file input with cropped image
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(croppedFile)
      this.uploadFieldTarget.files = dataTransfer.files

      const croppedImageURL = URL.createObjectURL(blob)
      
      // Call customizable post-crop handler
      this.onImageCropped(croppedImageURL, croppedFile)
      
      this.closeCropModal()
    }, "image/png")
  }

  // Remove image
  removeImage() {
    if (this.hasUploadFieldTarget) {
      this.uploadFieldTarget.value = ""
    }
    
    if (this.hasImagePreviewTarget) {
      this.imagePreviewTarget.src = ""
    }

    // Call customizable removal handler
    this.onImageRemoved()
  }

  // Validation
  isValidFileType(fileType) {
    return this.allowedTypes.includes(fileType)
  }

  // Error handling
  showError(message) {
    alert(message) // Override this for custom error display
  }

  // Customizable methods (override in extending classes)
  getCropperOptions() {
    return {
      aspectRatio: 16/9, // Default aspect ratio
      viewMode: 2,
      autoCropArea: 1,
      responsive: true,
      background: false,
      zoomable: true,
      scalable: true
    }
  }

  getErrorMessage(fileType) {
    return `Unsupported file type: ${fileType}. Please upload a valid image file (${this.allowedTypes.join(", ")}).`
  }

  // Utility methods
  showUploadArea() {
    if (this.hasUploadWrapperTarget) {
      this.uploadWrapperTarget.classList.remove("d-none")
    }
  }

  hideUploadArea() {
    if (this.hasUploadWrapperTarget) {
      this.uploadWrapperTarget.classList.add("d-none")
    }
  }

  showActionsArea() {
    if (this.hasActionsWrapperTarget) {
      this.actionsWrapperTarget.classList.remove("d-none")
    }
  }

  hideActionsArea() {
    if (this.hasActionsWrapperTarget) {
      this.actionsWrapperTarget.classList.add("d-none")
    }
  }

  setBackgroundImage(imageURL) {
    if (this.hasImageContainerTarget) {
      this.imageContainerTarget.style.backgroundImage = `url(${imageURL})`
    }
  }

  clearBackgroundImage() {
    if (this.hasImageContainerTarget) {
      this.imageContainerTarget.style.backgroundImage = ""
    }
  }
}